import { useEffect, useRef, useState } from "react";
import { api, euro, euroRound, formatApiErrorDetail } from "@/lib/api";
import { useCarViews, sortByViews, ViewsBadge, SortByViewsToggle } from "@/lib/carViews";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Switch } from "@/components/ui/switch";
import { NotificationsCenter } from "@/components/NotificationsCenter";
import { NewContractButton } from "@/components/NewContractButton";
import { Textarea } from "@/components/ui/textarea";
import { DEFAULT_NOTICE } from "@/components/PaymentNotice";
import { AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Car, LogOut, Plus, Pencil, Trash2, FileText, Loader2, ImagePlus, Bell, Home, X, CalendarDays, Printer, RefreshCw } from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import { BodyshopTab } from "@/components/BodyshopTab";
import { DeadlinesTab, isAlert } from "@/components/DeadlinesTab";
import { enablePush, clearAppBadge, pushSupported } from "@/lib/push";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const token = () => localStorage.getItem("an_token");

const fmtIt = (iso) => new Date(iso + "T00:00:00").toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" });
const blockRangeLabel = (from, to) => {
  const [y, m, d] = to.split("-").map(Number);
  const endIso = new Date(Date.UTC(y, m - 1, d - 1)).toISOString().slice(0, 10);
  return from === endIso ? fmtIt(from) : `dal ${fmtIt(from)} al ${fmtIt(endIso)}`;
};

function beep() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    if (ctx.state === "suspended") ctx.resume().catch(() => {});
    const tone = (start, freq) => {
      const o = ctx.createOscillator(); const g = ctx.createGain();
      o.connect(g); g.connect(ctx.destination); o.type = "square"; o.frequency.value = freq;
      g.gain.setValueAtTime(0.0001, start);
      g.gain.exponentialRampToValueAtTime(0.6, start + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, start + 0.35);
      o.start(start); o.stop(start + 0.4);
    };
    const t = ctx.currentTime;
    tone(t, 988); tone(t + 0.42, 1319); tone(t + 0.84, 988);
  } catch (e) { /* no-op */ }
  try { if (navigator.vibrate) navigator.vibrate([300, 150, 300, 150, 300]); } catch (e) { /* no-op */ }
}

function notifyNewBooking(b) {
  beep();
  toast.success(`Nuova prenotazione ${b.code}`, { description: `${b.car_name} · ${b.customer?.first_name || ""} ${b.customer?.last_name || ""}` });
  if ("Notification" in window && Notification.permission === "granted") {
    try { new Notification("🚗 Nuova prenotazione", { body: `${b.code} · ${b.car_name}\n${b.customer?.first_name || ""} ${b.customer?.last_name || ""} · ${b.customer?.phone || ""}`, tag: b.id }); } catch (e) { /* no-op */ }
  }
}

const statusColor = {
  pending: "bg-[#F59E0B]/20 text-[#F59E0B]", confirmed: "bg-[#10B981]/20 text-[#10B981]",
  cancelled: "bg-[#E63946]/20 text-[#E63946]", completed: "bg-[#3B82F6]/20 text-[#3B82F6]",
  paid: "bg-[#10B981]/20 text-[#10B981]", held: "bg-[#3B82F6]/20 text-[#3B82F6]",
  released: "bg-[#10B981]/20 text-[#10B981]", retained: "bg-[#E63946]/20 text-[#E63946]",
};
const SB = ({ v }) => <Badge className={`border-0 text-[10px] capitalize ${statusColor[v] || "bg-white/10 text-white"}`}>{v}</Badge>;

export default function AdminDashboard() {
  const { admin, logout } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [cars, setCars] = useState([]);
  const [saleCars, setSaleCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [sp] = useSearchParams();
  const [tab, setTab] = useState(sp.get("tab") || "bookings");
  const [newCount, setNewCount] = useState(0);
  const [dueCount, setDueCount] = useState(0);
  const countDue = (list) => setDueCount(list.filter(isAlert).length);
  useEffect(() => { api.get("/admin/deadlines").then((r) => countDue(r.data)).catch(() => {}); }, []);
  const knownIdsRef = useRef(null);

  const loadAll = async () => {
    setLoading(true); setLoadError(false);
    try {
      const [b, c, s] = await Promise.all([api.get("/admin/bookings"), api.get("/admin/cars"), api.get("/admin/sale-cars")]);
      setBookings(b.data); setCars(c.data); setSaleCars(s.data);
      if (knownIdsRef.current === null) knownIdsRef.current = new Set(b.data.map((x) => x.id));
    } catch (e) {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { loadAll(); }, []);

  // Live polling for new bookings: badge + sound + desktop notification
  useEffect(() => {
    // Auto re-subscribe to Web Push if the admin already granted permission, and clear the icon badge.
    if (pushSupported() && Notification.permission === "granted") {
      enablePush().catch(() => {});
    }
    clearAppBadge();
    const onFocus = () => clearAppBadge();
    window.addEventListener("focus", onFocus);
    const iv = setInterval(async () => {
      try {
        const { data } = await api.get("/admin/bookings");
        const known = knownIdsRef.current;
        if (known) {
          const fresh = data.filter((x) => !known.has(x.id));
          if (fresh.length) {
            fresh.forEach(notifyNewBooking);
            setNewCount((n) => n + fresh.length);
            setBookings(data);
          }
        }
        knownIdsRef.current = new Set(data.map((x) => x.id));
      } catch (e) { /* ignore transient errors */ }
    }, 15000);
    return () => { clearInterval(iv); window.removeEventListener("focus", onFocus); };
  }, []);

  useEffect(() => {
    document.title = newCount > 0 ? `(${newCount}) Nuove prenotazioni` : "Centro Auto Pontedera — Noleggio Auto";
  }, [newCount]);


  return (
    <div className="min-h-screen bg-background grain" data-testid="admin-dashboard">
      <header className="glass border-b border-white/10 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-20 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-[#E63946] flex items-center justify-center shrink-0"><Car className="w-5 h-5 sm:w-6 sm:h-6 text-white" /></div>
            <div className="min-w-0"><div className="font-display font-extrabold text-sm sm:text-lg uppercase leading-tight">Pannello Admin</div><div className="text-[11px] sm:text-xs text-muted-foreground truncate">{admin?.email}</div></div>
          </div>
          <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
            <Button asChild variant="outline" size="icon" className="rounded-full border-white/15 bg-transparent sm:w-auto sm:px-4" data-testid="back-to-site-btn" title="Torna al sito">
              <Link to="/"><Home className="w-4 h-4 sm:mr-2" /><span className="hidden sm:inline">Vai al sito</span></Link>
            </Button>
            <NotificationsCenter />
            <Button variant="outline" size="icon" onClick={logout} className="rounded-full border-white/15 bg-transparent sm:w-auto sm:px-4" data-testid="logout-btn" title="Esci"><LogOut className="w-4 h-4 sm:mr-2" /><span className="hidden sm:inline">Esci</span></Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-10">
        {loading ? <div className="flex justify-center py-32"><Loader2 className="w-8 h-8 animate-spin text-[#E63946]" /></div> : loadError ? (
          <div className="flex flex-col items-center justify-center py-32 gap-4" data-testid="admin-load-error">
            <p className="text-muted-foreground">Impossibile caricare i dati. Controlla la connessione e riprova.</p>
            <Button onClick={loadAll} className="bg-[#E63946] hover:bg-[#D62828] text-white rounded-full px-6" data-testid="admin-retry-btn">Riprova</Button>
          </div>
        ) : (
          <Tabs value={tab} onValueChange={(v) => { setTab(v); if (v === "bookings") setNewCount(0); }}>
            {dueCount > 0 && tab !== "deadlines" && (
              <button onClick={() => setTab("deadlines")} className="w-full mb-4 flex items-center gap-3 rounded-xl border border-[#E63946]/50 bg-[#E63946]/10 px-4 py-3 text-left text-sm hover:bg-[#E63946]/15 transition-colors" data-testid="deadlines-alert-banner">
                <CalendarDays className="w-5 h-5 text-[#E63946] shrink-0" />
                <span className="flex-1"><b>{dueCount} {dueCount === 1 ? "scadenza" : "scadenze"}</b> scadute o entro 7 giorni</span>
                <span className="text-[#E63946] font-semibold">Vedi</span>
              </button>
            )}
            <TabsList className="bg-[#181A22] border border-white/10 flex-wrap h-auto">
              <TabsTrigger value="bookings" data-testid="tab-bookings">
                Prenotazioni ({bookings.length})
                {newCount > 0 && <span className="ml-2 w-5 h-5 rounded-full bg-[#E63946] text-white text-[10px] inline-flex items-center justify-center font-bold">{newCount}</span>}
              </TabsTrigger>
              <TabsTrigger value="availability" data-testid="tab-availability">Disponibilità</TabsTrigger>
              <TabsTrigger value="fleet" data-testid="tab-fleet">Flotta ({cars.length})</TabsTrigger>
              <TabsTrigger value="sale" data-testid="tab-sale">Vendita ({saleCars.length})</TabsTrigger>
              <TabsTrigger value="bodyshop" data-testid="tab-bodyshop">Carrozzeria</TabsTrigger>
              <TabsTrigger value="deadlines" data-testid="tab-deadlines">
                Scadenze
                {dueCount > 0 && <span className="ml-2 w-5 h-5 rounded-full bg-[#E63946] text-white text-[10px] inline-flex items-center justify-center font-bold" data-testid="deadlines-badge">{dueCount}</span>}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="bookings" className="mt-6">
              <NewContractButton />
              <OnlinePaymentsToggle />
              <BookingsTab bookings={bookings} reload={loadAll} />
            </TabsContent>
            <TabsContent value="availability" className="mt-6">
              <AvailabilityTab />
            </TabsContent>
            <TabsContent value="fleet" className="mt-6">
              <FleetTab cars={cars} reload={loadAll} />
            </TabsContent>
            <TabsContent value="sale" className="mt-6">
              <SaleFleetTab cars={saleCars} reload={loadAll} />
            </TabsContent>
            <TabsContent value="bodyshop" className="mt-6">
              <BodyshopTab />
            </TabsContent>
            <TabsContent value="deadlines" className="mt-6">
              <DeadlinesTab cars={cars} onChange={countDue} />
            </TabsContent>
          </Tabs>
        )}
      </div>
    </div>
  );
}

/* ---------------- Bookings ---------------- */
function BookingsTab({ bookings, reload }) {
  if (bookings.length === 0) return <div className="text-center py-20 text-muted-foreground">Nessuna prenotazione.</div>;
  return (
    <div className="space-y-3">
      {bookings.map((b) => (
        <div key={b.id} className="rounded-xl border border-white/10 bg-[#181A22] p-5 flex flex-wrap items-center gap-4" data-testid={`booking-row-${b.code}`}>
          <div className="font-mono-tech text-sm text-[#E63946] font-semibold w-28">{b.code}</div>
          <div className="flex-1 min-w-[160px]">
            <div className="font-semibold">{b.car_name}</div>
            <div className="text-xs text-muted-foreground">{b.customer?.first_name} {b.customer?.last_name} · {b.pickup_date} → {b.return_date}</div>
          </div>
          <div className="text-sm font-display font-bold">{euro(b.total_price)}</div>
          <div className="flex gap-1.5 flex-wrap">
            <SB v={b.status} /><SB v={b.payment_status} />
            <Badge className="border-0 text-[10px] bg-white/10">{b.payment_method === "pay_online" ? "online" : "al ritiro"}</Badge>
          </div>
          <BookingDetail booking={b} reload={reload} />
          <Button asChild size="sm" variant="outline" className="rounded-full border-white/15 bg-transparent" data-testid={`contract-booking-${b.code}`} title="Apri e modifica il contratto">
            <Link to={`/admin/contratto/${b.id}`}><FileText className="w-4 h-4 mr-1.5" />Contratto</Link>
          </Button>
          <Button asChild size="sm" className="rounded-full bg-[#E63946] hover:bg-[#D62828] text-white" data-testid={`print-booking-${b.code}`} title="Stampa subito il contratto">
            <Link to={`/admin/contratto/${b.id}?stampa=1`}><Printer className="w-4 h-4 mr-1.5" />Stampa</Link>
          </Button>
          <DeleteBookingButton booking={b} reload={reload} />
        </div>
      ))}
    </div>
  );
}

function DeleteBookingButton({ booking, reload }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const remove = async () => {
    setBusy(true);
    try {
      await api.delete(`/admin/bookings/${booking.id}`);
      toast.success("Prenotazione eliminata");
      setOpen(false);
      reload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>
        <Button size="sm" variant="outline" className="rounded-full border-white/15 bg-transparent text-[#E63946]" data-testid={`delete-booking-${booking.code}`} title="Elimina">
          <Trash2 className="w-4 h-4" />
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent className="bg-[#12141A] border-white/10">
        <AlertDialogHeader>
          <AlertDialogTitle className="font-display uppercase">Eliminare la prenotazione?</AlertDialogTitle>
          <AlertDialogDescription>
            Stai per eliminare definitivamente la prenotazione <b className="text-foreground">{booking.code}</b> ({booking.car_name}). L'azione non è reversibile.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className="rounded-full bg-transparent border-white/15" data-testid="cancel-delete-booking">Annulla</AlertDialogCancel>
          <Button onClick={remove} disabled={busy} className="rounded-full bg-[#E63946] hover:bg-[#D62828] text-white" data-testid="confirm-delete-booking">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Elimina"}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function BookingDetail({ booking, reload }) {
  const [open, setOpen] = useState(false);
  const [b, setB] = useState(booking);
  const docs = b.documents || {};
  const docLabels = { license_front: "Patente fronte", license_back: "Patente retro", id_front: "CI fronte", id_back: "CI retro" };

  const update = async (field, value) => {
    try {
      const { data } = await api.put(`/admin/bookings/${b.id}/status`, { [field]: value });
      setB(data); reload(); toast.success("Aggiornato");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" variant="outline" className="rounded-full border-white/15 bg-transparent" data-testid={`view-booking-${b.code}`}><FileText className="w-4 h-4 mr-1.5" />Dettagli</Button></DialogTrigger>
      <DialogContent className="bg-[#12141A] border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display uppercase">Prenotazione {b.code}</DialogTitle></DialogHeader>
        <div className="space-y-5">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <Field label="Cliente" value={`${b.customer?.first_name} ${b.customer?.last_name}`} />
            <Field label="Email" value={b.customer?.email} />
            <Field label="Telefono" value={b.customer?.phone} />
            <Field label="Auto" value={b.car_name} />
            <Field label="Periodo" value={`${b.pickup_date} → ${b.return_date} (${b.days}gg)`} />
            <Field label="Totale" value={euro(b.total_price)} />
            <Field label="Cauzione" value={euro(b.deposit)} />
            <Field label="Pagamento" value={b.payment_method === "pay_online" ? "Online" : "Al ritiro"} />
          </div>

          <div className="grid sm:grid-cols-3 gap-3">
            <Ctrl label="Stato" value={b.status} options={["pending", "confirmed", "cancelled", "completed"]} onChange={(v) => update("status", v)} testid="set-status" />
            <Ctrl label="Pagamento" value={b.payment_status} options={["pending", "paid"]} onChange={(v) => update("payment_status", v)} testid="set-payment" />
            <Ctrl label="Cauzione" value={b.deposit_status} options={["pending", "held", "released", "retained"]} onChange={(v) => update("deposit_status", v)} testid="set-deposit" />
          </div>

          <div>
            <div className="font-mono-tech text-xs tracking-widest uppercase text-muted-foreground mb-3">Documenti caricati</div>
            {Object.keys(docs).length === 0 ? <p className="text-sm text-muted-foreground">Nessun documento caricato.</p> : (
              <div className="grid grid-cols-2 gap-3">
                {Object.keys(docs).map((k) => (
                  <div key={k} className="rounded-lg border border-white/10 overflow-hidden" data-testid={`doc-${k}`}>
                    <div className="text-xs px-3 py-2 bg-white/5">{docLabels[k] || k}</div>
                    <img src={`${BACKEND}/api/admin/documents/${b.id}/${k}?auth=${token()}`} alt={k} className="w-full aspect-[16/10] object-cover bg-black" />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const Field = ({ label, value }) => (<div><div className="text-[11px] text-muted-foreground uppercase tracking-wider">{label}</div><div className="font-medium">{value || "—"}</div></div>);

function Ctrl({ label, value, options, onChange, testid }) {
  return (
    <div>
      <Label className="text-xs text-muted-foreground">{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="bg-[#181A22] border-white/10 mt-1.5" data-testid={testid}><SelectValue /></SelectTrigger>
        <SelectContent>{options.map((o) => <SelectItem key={o} value={o} className="capitalize">{o}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );
}

/* ---------------- Availability ---------------- */
function AvailabilityTab() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [carId, setCarId] = useState("");
  const [range, setRange] = useState(undefined);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [days, setDays] = useState(123);
  const [pending, setPending] = useState(new Set());
  const [clientName, setClientName] = useState("");

  const load = () => api.get(`/admin/availability?days=${days}`).then((r) => setData(r.data)).catch(() => setErr(true));
  useEffect(() => { load(); }, [days]);

  if (err) return <div className="text-center py-20 text-muted-foreground" data-testid="avail-error">Impossibile caricare la disponibilità.</div>;
  if (!data) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#E63946]" /></div>;
  const today = data.start_date;
  const fmt = (iso) => { const d = new Date(iso + "T00:00:00"); return { dd: d.getDate(), mm: d.toLocaleDateString("it-IT", { month: "short" }), wd: d.toLocaleDateString("it-IT", { weekday: "short" }) }; };

  const createBlock = async () => {
    if (!carId) { toast.error("Seleziona un'auto"); return; }
    if (!range || !range.from) { toast.error("Seleziona le date"); return; }
    const toISO = (d) => { const x = new Date(d); x.setHours(12, 0, 0, 0); return x.toISOString().slice(0, 10); };
    const from = range.from;
    const last = range.to || range.from;
    const end = new Date(last); end.setDate(end.getDate() + 1);
    setSaving(true);
    try {
      await api.post("/admin/blocks", { car_id: carId, from_date: toISO(from), to_date: toISO(end), reason });
      toast.success("Periodo bloccato con successo");
      setRange(undefined); setReason("");
      await load();
    } catch (ex) {
      toast.error(formatApiErrorDetail(ex.response?.data?.detail) || "Impossibile bloccare il periodo");
    } finally { setSaving(false); }
  };

  const deleteBlock = async (id) => {
    try { await api.delete(`/admin/blocks/${id}`); toast.success("Blocco rimosso"); await load(); }
    catch { toast.error("Impossibile rimuovere il blocco"); }
  };

  const toggleDay = async (c, d) => {
    if (new Set(c.booked_dates).has(d)) return;
    const key = `${c.id}|${d}`;
    if (pending.has(key)) return;
    const covering = (c.blocks || []).find((b) => b.from <= d && d < b.to);
    if (covering) {
      const span = Math.round((new Date(covering.to) - new Date(covering.from)) / 86400000);
      if (span > 1 && !window.confirm(`Questo blocco copre più giorni (dal ${covering.from} al ${covering.to} escluso). Vuoi rimuoverlo tutto?`)) return;
    }
    setPending((p) => new Set(p).add(key));
    try {
      if (covering) {
        await api.delete(`/admin/blocks/${covering.id}`);
        toast.success("Giorno sbloccato");
      } else {
        const [yy, mm, dd] = d.split("-").map(Number);
        const to = new Date(Date.UTC(yy, mm - 1, dd + 1)).toISOString().slice(0, 10);
        const reason = clientName.trim() || "Carrozzeria";
        await api.post("/admin/blocks", { car_id: c.id, from_date: d, to_date: to, reason });
        toast.success(`Giorno bloccato${clientName.trim() ? ` · ${clientName.trim()}` : " (Carrozzeria)"}`);
      }
      await load();
    } catch (ex) {
      toast.error(formatApiErrorDetail(ex.response?.data?.detail) || "Operazione non riuscita");
    } finally {
      setPending((p) => { const n = new Set(p); n.delete(key); return n; });
    }
  };

  const allBlocks = data.cars.flatMap((c) => (c.blocks || []).map((b) => ({ ...b, car_name: c.name, plate: c.plate })));

  return (
    <div data-testid="availability-tab">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4 mb-8">
        {data.cars.map((c) => {
          const bset = new Set(c.booked_dates);
          const blset = new Set(c.blocked_dates || []);
          const busyNow = bset.has(today);
          const blockedNow = blset.has(today);
          const upcoming = (c.ranges || []).filter((r) => r.to > today).sort((a, b) => a.from.localeCompare(b.from));
          const current = upcoming.find((r) => r.from <= today && r.to > today);
          const next = upcoming.find((r) => r.from > today);
          const curBlock = (c.blocks || []).find((b) => b.from <= today && b.to > today);
          const state = busyNow ? "busy" : blockedNow ? "blocked" : "free";
          const styles = { busy: "border-[#E63946]/40 bg-[#E63946]/10", blocked: "border-[#FF9E00]/40 bg-[#FF9E00]/10", free: "border-[#10B981]/30 bg-[#10B981]/5" };
          const badge = { busy: "bg-[#E63946] text-white", blocked: "bg-[#FF9E00] text-black", free: "bg-[#10B981] text-white" };
          const label = { busy: "OCCUPATA", blocked: "BLOCCATA", free: "LIBERA" };
          return (
            <div key={c.id} className={`rounded-xl border p-4 ${styles[state]}`} data-testid={`avail-card-${c.plate || c.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="font-semibold leading-tight">{c.name}</div>
                  <div className="text-[10px] text-muted-foreground font-mono-tech">{c.plate}</div>
                </div>
                <span className={`shrink-0 text-[10px] font-bold px-2 py-1 rounded-full ${badge[state]}`}>{label[state]}</span>
              </div>
              <div className="mt-3 text-xs text-muted-foreground min-h-[2.5rem]">
                {blockedNow && curBlock ? (
                  <>Bloccata fino al <span className="text-foreground font-semibold">{curBlock.to}</span>{curBlock.reason ? ` · ${curBlock.reason}` : ""}</>
                ) : busyNow && current ? (
                  <>Occupata fino al <span className="text-foreground font-semibold">{current.to}</span>{current.customer ? ` · ${current.customer}` : ""}</>
                ) : next ? (
                  <>Prossima prenotazione: <span className="text-foreground font-semibold">{next.from}</span>{next.customer ? ` · ${next.customer}` : ""}</>
                ) : (
                  <>Disponibile, nessuna prenotazione in arrivo 🎉</>
                )}
              </div>
              {upcoming.length > 0 && (
                <div className="mt-2 text-[10px] text-muted-foreground">{upcoming.length} prenotazione/i in programma</div>
              )}
            </div>
          );
        })}
      </div>

      {/* Blocca date manualmente */}
      <div className="rounded-xl border border-white/10 bg-[#181A22] p-5 mb-8" data-testid="block-form">
        <div className="font-display font-bold uppercase text-sm mb-1">Blocca un'auto (manutenzione / ferie)</div>
        <p className="text-xs text-muted-foreground mb-4">Rendi un veicolo non prenotabile in un periodo, senza creare una prenotazione. Seleziona il primo e l'ultimo giorno da bloccare (inclusi).</p>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5 lg:items-end">
          <div className="lg:col-span-2">
            <Label className="text-xs">Auto</Label>
            <Select value={carId} onValueChange={setCarId}>
              <SelectTrigger className="bg-[#12141A] border-white/10 mt-1.5" data-testid="block-car-select"><SelectValue placeholder="Seleziona auto" /></SelectTrigger>
              <SelectContent>{data.cars.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} ({c.plate})</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="lg:col-span-2">
            <Label className="text-xs">Periodo da bloccare</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" data-testid="block-daterange" className="w-full justify-start bg-[#12141A] border-white/10 mt-1.5 font-normal hover:bg-[#12141A]">
                  <CalendarDays className="w-4 h-4 mr-2 text-[#FF9E00] shrink-0" />
                  {range?.from
                    ? (range.to && range.to.getTime() !== range.from.getTime()
                        ? `${range.from.toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit" })} → ${range.to.toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit" })}`
                        : range.from.toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" }))
                    : <span className="text-muted-foreground">Scegli le date</span>}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start" data-testid="block-calendar">
                <Calendar mode="range" selected={range} onSelect={setRange} numberOfMonths={1} disabled={{ before: new Date(today + "T00:00:00") }} initialFocus />
              </PopoverContent>
            </Popover>
          </div>
          <Button onClick={createBlock} disabled={saving} className="bg-[#FF9E00] hover:bg-[#e68f00] text-black rounded-full font-semibold" data-testid="block-submit">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Blocca periodo"}
          </Button>
        </div>
        <div className="mt-3">
          <Label className="text-xs">Motivo (facoltativo)</Label>
          <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Es. manutenzione, ferie, tagliando…" className="bg-[#12141A] border-white/10 mt-1.5" data-testid="block-reason" />
        </div>
        {allBlocks.length > 0 && (
          <div className="mt-5 space-y-2" data-testid="block-list">
            <div className="text-xs text-muted-foreground font-mono-tech uppercase">Blocchi attivi</div>
            {allBlocks.sort((a, b) => a.from.localeCompare(b.from)).map((b) => (
              <div key={b.id} className="flex items-center justify-between gap-3 rounded-lg bg-[#FF9E00]/10 border border-[#FF9E00]/20 px-3 py-2" data-testid={`block-item-${b.id}`}>
                <div className="text-xs">
                  <span className="font-semibold">{b.car_name}</span> <span className="text-muted-foreground">({b.plate})</span>
                  <span className="text-[#FF9E00] ml-2">{blockRangeLabel(b.from, b.to)}</span>
                  {b.reason ? <span className="text-muted-foreground"> · {b.reason}</span> : null}
                </div>
                <Button size="icon" variant="ghost" onClick={() => deleteBlock(b.id)} className="h-7 w-7 text-muted-foreground hover:text-[#E63946]" data-testid={`block-delete-${b.id}`}><Trash2 className="w-4 h-4" /></Button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="rounded-xl border border-white/10 bg-[#181A22] p-4 mb-4" data-testid="calendar-controls">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground font-mono-tech uppercase mr-1">Periodo:</span>
            {[{ m: 1, d: 31 }, { m: 2, d: 62 }, { m: 3, d: 92 }, { m: 4, d: 123 }].map((o) => (
              <button key={o.m} onClick={() => setDays(o.d)} data-testid={`cal-range-${o.m}m`}
                className={`text-xs font-semibold rounded-full px-3 py-1.5 transition-colors ${days === o.d ? "bg-[#E63946] text-white" : "bg-white/5 text-muted-foreground hover:text-foreground"}`}>
                {o.m} mes{o.m === 1 ? "e" : "i"}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-4 text-xs">
            <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-[#10B981]/40 inline-block" /> Libera</span>
            <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-[#E63946]/70 inline-block" /> Occupata</span>
            <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-[#FF9E00]/70 inline-block" /> Bloccata</span>
          </div>
        </div>
        <div className="mt-3 max-w-sm">
          <Label className="text-xs">Cliente carrozzeria (facoltativo)</Label>
          <Input value={clientName} onChange={(e) => setClientName(e.target.value)} placeholder="Es. Mario Rossi · Golf XX000XX" className="bg-[#12141A] border-white/10 mt-1.5" data-testid="cal-client-name" />
          <p className="text-[11px] text-muted-foreground mt-1.5">Scrivi qui il nome: i giorni che blocchi verranno etichettati con questo nome (altrimenti "Carrozzeria"). Passa il mouse su un giorno bloccato per vederlo.</p>
        </div>
        <p className="text-xs text-muted-foreground mt-3">👆 Clicca un giorno <span className="text-[#10B981]">libero</span> per bloccarlo, oppure un giorno <span className="text-[#FF9E00]">bloccato</span> per sbloccarlo. I giorni <span className="text-[#E63946]">occupati</span> da prenotazioni clienti non sono modificabili. Scorri lateralmente per vedere tutto il periodo.</p>
      </div>
      <div className="overflow-x-auto rounded-xl border border-white/10 bg-[#181A22]">
        <table className="min-w-max text-xs">
          <thead>
            <tr>
              <th className="sticky left-0 bg-[#181A22] z-10 text-left p-3 font-mono-tech text-muted-foreground">AUTO</th>
              {data.dates.map((d) => { const f = fmt(d); return (
                <th key={d} className={`p-2 text-center font-normal ${d === today ? "text-[#FF9E00]" : "text-muted-foreground"}`}>
                  <div className="uppercase text-[9px]">{f.wd}</div><div className="font-bold text-foreground text-sm">{f.dd}</div><div className="uppercase text-[9px]">{f.mm}</div>
                </th>); })}
            </tr>
          </thead>
          <tbody>
            {data.cars.map((c) => {
              const bset = new Set(c.booked_dates);
              const blset = new Set(c.blocked_dates || []);
              return (
                <tr key={c.id} className="border-t border-white/5" data-testid={`avail-row-${c.plate || c.id}`}>
                  <td className="sticky left-0 bg-[#181A22] z-10 p-3 whitespace-nowrap">
                    <div className="font-semibold">{c.name}</div><div className="text-[10px] text-muted-foreground font-mono-tech">{c.plate}</div>
                  </td>
                  {data.dates.map((d) => {
                    const booked = bset.has(d);
                    const blocked = blset.has(d);
                    const key = `${c.id}|${d}`;
                    const isPending = pending.has(key);
                    const covering = blocked ? (c.blocks || []).find((b) => b.from <= d && d < b.to) : null;
                    const cls = booked ? "bg-[#E63946]/70" : blocked ? "bg-[#FF9E00]/70" : "bg-[#10B981]/25";
                    const tt = booked ? "Occupata (prenotazione cliente)" : blocked ? `Bloccata${covering && covering.reason ? ` · ${covering.reason}` : ""} — clicca per sbloccare` : "Libera — clicca per bloccare";
                    return (
                      <td key={d} className="p-1.5">
                        <button type="button" disabled={booked || isPending} onClick={() => toggleDay(c, d)} title={tt}
                          data-testid={`avail-cell-${c.plate || c.id}-${d}`}
                          className={`w-7 h-7 mx-auto rounded block transition-all ${cls} ${booked ? "cursor-not-allowed" : "cursor-pointer hover:ring-2 hover:ring-white/50"} ${isPending ? "opacity-40" : ""}`} />
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="mt-6 grid gap-3">
        {data.cars.filter((c) => c.ranges.length > 0).map((c) => (
          <div key={c.id} className="rounded-xl border border-white/10 bg-[#181A22] p-4">
            <div className="font-semibold mb-2">{c.name} <span className="text-xs text-muted-foreground">({c.plate})</span></div>
            <div className="flex flex-wrap gap-2">
              {c.ranges.map((r, i) => (
                <span key={i} className="text-xs px-2.5 py-1 rounded-full bg-[#E63946]/15 text-[#E63946]">{r.from} → {r.to}{r.customer ? ` · ${r.customer}` : ""}</span>
              ))}
            </div>
          </div>
        ))}
        {data.cars.every((c) => c.ranges.length === 0) && <p className="text-sm text-muted-foreground text-center py-4">Nessuna prenotazione attiva: tutte le auto sono libere. 🎉</p>}
      </div>
    </div>
  );
}

/* ---------------- Fleet ---------------- */
const EMPTY = { brand: "", model: "", category: "SUV", transmission: "Automatico", fuel: "Benzina", seats: 5, daily_rate: 50, deposit: 500, image_url: "", status: "available", plate: "", description: "" };

function FleetTab({ cars, reload }) {
  const views = useCarViews("rent");
  const [byViews, setByViews] = useState(false);
  return (
    <div>
      <div className="flex flex-wrap items-center justify-end gap-3 mb-5"><SortByViewsToggle on={byViews} setOn={setByViews} id="fleet" /><CarForm reload={reload} trigger={<Button className="bg-[#E63946] hover:bg-[#D62828] text-white rounded-full" data-testid="add-car-btn"><Plus className="w-4 h-4 mr-2" />Aggiungi auto</Button>} /></div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {sortByViews(cars, views, byViews).map((c) => (
          <div key={c.id} className="rounded-xl border border-white/10 bg-[#181A22] overflow-hidden" data-testid={`fleet-car-${c.id}`}>
            <img src={c.image_url} alt="" className="w-full aspect-[16/10] object-cover" />
            <div className="p-4">
              <div className="flex items-center justify-between">
                <div className="font-display font-bold">{c.brand} {c.model}</div>
                <SB v={c.status} />
              </div>
              <div className="text-xs text-muted-foreground mt-1">{euro(c.daily_rate)}/g · Cauzione {euro(c.deposit)}</div>
              <ViewsBadge v={views[c.id]} id={`fleet-${c.id}`} />
              <div className="flex gap-2 mt-4">
                <CarForm reload={reload} car={c} trigger={<Button size="sm" variant="outline" className="flex-1 rounded-full border-white/15 bg-transparent" data-testid={`edit-car-${c.id}`}><Pencil className="w-3.5 h-3.5 mr-1.5" />Modifica</Button>} />
                <DeleteCarButton car={c} reload={reload} />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function DeleteCarButton({ car, reload }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const remove = async () => {
    setBusy(true);
    try {
      await api.delete(`/admin/cars/${car.id}`);
      toast.success("Auto eliminata");
      setOpen(false);
      reload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>
        <Button size="sm" variant="outline" className="rounded-full border-white/15 bg-transparent text-[#E63946]" data-testid={`delete-car-${car.id}`} title="Elimina"><Trash2 className="w-3.5 h-3.5" /></Button>
      </AlertDialogTrigger>
      <AlertDialogContent className="bg-[#12141A] border-white/10">
        <AlertDialogHeader>
          <AlertDialogTitle className="font-display uppercase">Eliminare l'auto?</AlertDialogTitle>
          <AlertDialogDescription>Stai per rimuovere <b className="text-foreground">{car.brand} {car.model}</b> dalla flotta. L'azione non è reversibile.</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className="rounded-full bg-transparent border-white/15" data-testid="cancel-delete-car">Annulla</AlertDialogCancel>
          <Button onClick={remove} disabled={busy} className="rounded-full bg-[#E63946] hover:bg-[#D62828] text-white" data-testid="confirm-delete-car">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Elimina"}</Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function CarForm({ car, reload, trigger }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(car || EMPTY);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setBusy(true);
    const body = { ...f, seats: Number(f.seats), daily_rate: Number(f.daily_rate), deposit: Number(f.deposit) };
    try {
      if (car) await api.put(`/admin/cars/${car.id}`, body);
      else await api.post("/admin/cars", body);
      toast.success(car ? "Auto aggiornata" : "Auto creata");
      setOpen(false); reload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) setF(car || EMPTY); }}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="bg-[#12141A] border-white/10 max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display uppercase">{car ? "Modifica auto" : "Nuova auto"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <In label="Marca" v={f.brand} on={(v) => set("brand", v)} id="car-brand" />
          <In label="Modello" v={f.model} on={(v) => set("model", v)} id="car-model" />
          <Sel label="Categoria" v={f.category} on={(v) => set("category", v)} opts={["City", "SUV", "Station Wagon", "Sportiva", "Berlina"]} />
          <Sel label="Cambio" v={f.transmission} on={(v) => set("transmission", v)} opts={["Automatico", "Manuale"]} />
          <Sel label="Carburante" v={f.fuel} on={(v) => set("fuel", v)} opts={["Benzina", "Diesel", "Ibrida", "Elettrica"]} />
          <In label="Posti" type="number" v={f.seats} on={(v) => set("seats", v)} id="car-seats" />
          <In label="Tariffa/giorno €" type="number" v={f.daily_rate} on={(v) => set("daily_rate", v)} id="car-rate" />
          <In label="Cauzione €" type="number" v={f.deposit} on={(v) => set("deposit", v)} id="car-deposit" />
          <In label="Targa" v={f.plate} on={(v) => set("plate", v)} id="car-plate" />
          <Sel label="Stato" v={f.status} on={(v) => set("status", v)} opts={["available", "rented", "maintenance"]} />
          <div className="col-span-2">
            <Label className="text-xs text-muted-foreground">Foto auto</Label>
            <CarImageUpload value={f.image_url} onChange={(v) => set("image_url", v)} />
            <Input value={f.image_url} onChange={(e) => set("image_url", e.target.value)} placeholder="oppure incolla un URL immagine" className="bg-[#181A22] border-white/10 mt-2 text-xs" data-testid="car-image" />
          </div>
          <div className="col-span-2"><In label="Descrizione" v={f.description} on={(v) => set("description", v)} id="car-desc" /></div>
        </div>
        <DialogFooter><Button onClick={save} disabled={busy} className="bg-[#E63946] hover:bg-[#D62828] text-white rounded-full" data-testid="save-car-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Salva"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const In = ({ label, v, on, type = "text", id }) => (<div><Label className="text-xs text-muted-foreground">{label}</Label><Input type={type} value={v} onChange={(e) => on(e.target.value)} className="bg-[#181A22] border-white/10 mt-1.5" data-testid={id} /></div>);
const Sel = ({ label, v, on, opts }) => (<div><Label className="text-xs text-muted-foreground">{label}</Label><Select value={v} onValueChange={on}><SelectTrigger className="bg-[#181A22] border-white/10 mt-1.5"><SelectValue /></SelectTrigger><SelectContent>{opts.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent></Select></div>);

function CarImageUpload({ value, onChange }) {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const handle = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/cars/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(`${BACKEND}/api/car-images/${data.path}`);
      toast.success("Foto caricata");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="mt-1.5">
      <button type="button" onClick={() => ref.current?.click()}
        className="w-full aspect-[16/9] rounded-lg border-2 border-dashed border-white/15 hover:border-[#E63946]/60 transition-colors flex items-center justify-center overflow-hidden relative bg-black/30"
        data-testid="car-image-upload-btn">
        {value ? <img src={value} alt="" className="w-full h-full object-cover" /> :
          <div className="flex flex-col items-center gap-2 text-muted-foreground"><ImagePlus className="w-7 h-7" /><span className="text-xs">Carica foto auto</span></div>}
        {busy && <div className="absolute inset-0 bg-black/60 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-white" /></div>}
      </button>
      <input ref={ref} type="file" accept="image/*" className="hidden" onChange={handle} />
    </div>
  );
}


/* ---------------- Cars for sale ---------------- */
const EMPTY_SALE = { brand: "", model: "", year: "", mileage: "", fuel: "Benzina", transmission: "Manuale", power: "", condition: "Usato", price: "", price_on_request: false, description: "", images: [], sold: false, status: "active" };

function SaleFleetTab({ cars, reload }) {
  const views = useCarViews("sale");
  const [byViews, setByViews] = useState(false);
  return (
    <div data-testid="sale-fleet-tab">
      <div className="flex flex-wrap items-center justify-end gap-3 mb-5"><SyncSaleCars reload={reload} /><SortByViewsToggle on={byViews} setOn={setByViews} id="sale" /><SaleCarForm reload={reload} trigger={<Button className="bg-[#E63946] hover:bg-[#D62828] text-white rounded-full" data-testid="add-sale-car-btn"><Plus className="w-4 h-4 mr-2" />Aggiungi auto in vendita</Button>} /></div>
      {cars.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground">Nessuna auto in vendita. Aggiungi la prima!</div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {sortByViews(cars, views, byViews).map((c) => (
            <div key={c.id} className="rounded-xl border border-white/10 bg-[#181A22] overflow-hidden" data-testid={`sale-fleet-car-${c.id}`}>
              <div className="relative aspect-[16/10] bg-black">
                {c.images?.[0] ? <img src={c.images[0]} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-white/20"><Car className="w-10 h-10" /></div>}
                {c.sold && <span className="absolute top-2 right-2 bg-[#E63946] text-white text-[10px] font-bold px-2 py-1 rounded-full">VENDUTA</span>}
              </div>
              <div className="p-4">
                <div className="font-display font-bold">{c.brand} {c.model} {c.year ? <span className="text-xs text-muted-foreground">· {c.year}</span> : null}</div>
                <div className="text-xs text-muted-foreground mt-1">{c.price_on_request || !c.price ? "Trattativa riservata" : euroRound(c.price)} · {c.images?.length || 0} foto</div>
                <ViewsBadge v={views[c.id]} id={`sale-${c.id}`} />
                <div className="flex gap-2 mt-4">
                  <SaleCarForm reload={reload} car={c} trigger={<Button size="sm" variant="outline" className="flex-1 rounded-full border-white/15 bg-transparent" data-testid={`edit-sale-car-${c.id}`}><Pencil className="w-3.5 h-3.5 mr-1.5" />Modifica</Button>} />
                  <DeleteSaleCarButton car={c} reload={reload} />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function OnlinePaymentsToggle() {
  const [on, setOn] = useState(null);
  const [notice, setNotice] = useState(DEFAULT_NOTICE);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/config").then((r) => { setOn(!!r.data.online_payments); setNotice({ ...DEFAULT_NOTICE, ...Object.fromEntries(Object.entries(r.data.notice || {}).filter(([, v]) => v)) }); }).catch(() => {});
  }, []);
  const saveNotice = async () => {
    if (!notice.title.trim() || !notice.text.trim()) { toast.error("Titolo e testo non possono essere vuoti"); return; }
    setSaving(true);
    try { await api.put("/admin/settings/payment-notice", notice); toast.success("Testo dell'avviso aggiornato sul sito"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const change = async (v) => {
    setOn(v);
    try {
      await api.put("/admin/settings/online-payments", { enabled: v });
      toast.success(v ? "Pagamento online attivato sul sito" : "Pagamento online spento: i clienti vedono l'avviso 'paga in sede'");
    } catch (e) { setOn(!v); toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  if (on === null) return null;
  return (
    <div className={`mb-5 rounded-2xl border p-4 flex items-start gap-4 ${on ? "border-[#10B981]/30 bg-[#10B981]/5" : "border-[#FDE68A]/30 bg-[#FDE68A]/5"}`} data-testid="online-payments-toggle-box">
      <Switch checked={on} onCheckedChange={change} className="data-[state=unchecked]:bg-white/25 data-[state=checked]:bg-[#10B981]" data-testid="online-payments-switch" />
      <div className="min-w-0">
        <div className="font-semibold text-sm">Pagamento online sul sito: {on ? "ATTIVO" : "SPENTO"}</div>
        <div className="text-xs text-muted-foreground">{on ? "I clienti possono pagare con carta durante la prenotazione." : "I clienti vedono un avviso e possono solo pagare in sede al ritiro. Attivalo quando Stripe è pronto per incassare."}</div>
        {!on && (
          <div className="mt-3 grid gap-2 max-w-xl" data-testid="payment-notice-editor">
            <Label className="text-xs">Testo del post-it giallo</Label>
            <Input value={notice.title} maxLength={120} onChange={(e) => setNotice((n) => ({ ...n, title: e.target.value }))} className="bg-[#0B0C10] border-white/10" data-testid="payment-notice-title-input" />
            <Textarea value={notice.text} maxLength={600} rows={3} onChange={(e) => setNotice((n) => ({ ...n, text: e.target.value }))} className="bg-[#0B0C10] border-white/10" data-testid="payment-notice-text-input" />
            <div className="flex gap-2">
              <Button size="sm" onClick={saveNotice} disabled={saving} className="rounded-full bg-[#E63946] hover:bg-[#D62828] text-white" data-testid="payment-notice-save-btn">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Salva testo"}</Button>
              <Button size="sm" variant="outline" onClick={() => setNotice(DEFAULT_NOTICE)} className="rounded-full border-white/15 bg-transparent" data-testid="payment-notice-default-btn">Testo predefinito</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function SyncSaleCars({ reload }) {
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/admin/sale-cars/sync-status").then(({ data }) => setSt(data)).catch(() => {}); }, []);
  const sync = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/sale-cars/sync");
      setSt(data); reload();
      toast.success(`Sincronizzate ${data.total} auto da AutoSupermarket`, { description: `${data.added} nuove · ${data.updated} aggiornate · ${data.sold} segnate come vendute` });
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const when = st?.at ? new Date(st.at).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : null;
  return (
    <div className="flex items-center gap-3 mr-auto">
      <Button onClick={sync} disabled={busy} variant="outline" className="rounded-full border-white/15 bg-transparent" data-testid="sync-sale-cars-btn">
        <RefreshCw className={`w-4 h-4 mr-2 ${busy ? "animate-spin" : ""}`} />{busy ? "Sincronizzazione…" : "Sincronizza ora"}
      </Button>
      <span className="text-xs text-muted-foreground" data-testid="sync-sale-cars-status">
        {when ? (st.ok ? `Ultimo aggiornamento AutoSupermarket: ${when}` : `Ultimo tentativo fallito (${when})`) : "Aggiornamento automatico ogni notte alle 3:00"}
      </span>
    </div>
  );
}

function DeleteSaleCarButton({ car, reload }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const remove = async () => {
    setBusy(true);
    try {
      await api.delete(`/admin/sale-cars/${car.id}`);
      toast.success("Auto rimossa dalla vendita");
      setOpen(false); reload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>
        <Button size="sm" variant="outline" className="rounded-full border-white/15 bg-transparent text-[#E63946]" data-testid={`delete-sale-car-${car.id}`} title="Elimina"><Trash2 className="w-3.5 h-3.5" /></Button>
      </AlertDialogTrigger>
      <AlertDialogContent className="bg-[#12141A] border-white/10">
        <AlertDialogHeader>
          <AlertDialogTitle className="font-display uppercase">Eliminare l'auto?</AlertDialogTitle>
          <AlertDialogDescription>Stai per rimuovere <b className="text-foreground">{car.brand} {car.model}</b> dalla vetrina vendita.</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className="rounded-full bg-transparent border-white/15" data-testid="cancel-delete-sale-car">Annulla</AlertDialogCancel>
          <Button onClick={remove} disabled={busy} className="rounded-full bg-[#E63946] hover:bg-[#D62828] text-white" data-testid="confirm-delete-sale-car">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Elimina"}</Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function SaleCarForm({ car, reload, trigger }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(car || EMPTY_SALE);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const save = async () => {
    if (!f.brand || !f.model) { toast.error("Inserisci marca e modello"); return; }
    setBusy(true);
    const body = {
      ...f,
      year: f.year ? Number(f.year) : null,
      mileage: f.mileage !== "" && f.mileage != null ? Number(f.mileage) : null,
      price: f.price_on_request || f.price === "" || f.price == null ? null : Number(f.price),
      images: f.images || [],
    };
    try {
      if (car) await api.put(`/admin/sale-cars/${car.id}`, body);
      else await api.post("/admin/sale-cars", body);
      toast.success(car ? "Auto aggiornata" : "Auto aggiunta alla vendita");
      setOpen(false); reload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) setF(car || EMPTY_SALE); }}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="bg-[#12141A] border-white/10 max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display uppercase">{car ? "Modifica auto in vendita" : "Nuova auto in vendita"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <In label="Marca" v={f.brand} on={(v) => set("brand", v)} id="sale-brand" />
          <In label="Modello" v={f.model} on={(v) => set("model", v)} id="sale-model" />
          <In label="Anno" type="number" v={f.year} on={(v) => set("year", v)} id="sale-year" />
          <In label="Chilometri" type="number" v={f.mileage} on={(v) => set("mileage", v)} id="sale-mileage" />
          <Sel label="Alimentazione" v={f.fuel} on={(v) => set("fuel", v)} opts={["Benzina", "Diesel", "Ibrida", "Elettrica", "GPL", "Metano"]} />
          <Sel label="Cambio" v={f.transmission} on={(v) => set("transmission", v)} opts={["Manuale", "Automatico"]} />
          <In label="Potenza (es. 110 CV)" v={f.power} on={(v) => set("power", v)} id="sale-power" />
          <Sel label="Stato" v={f.condition} on={(v) => set("condition", v)} opts={["Nuovo", "Km 0", "Usato"]} />
          <In label="Prezzo €" type="number" v={f.price} on={(v) => set("price", v)} id="sale-price" />
          <div className="flex items-end pb-1.5">
            <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="sale-price-on-request">
              <input type="checkbox" checked={!!f.price_on_request} onChange={(e) => set("price_on_request", e.target.checked)} className="w-4 h-4 accent-[#E63946]" />
              Trattativa riservata
            </label>
          </div>
          <div className="col-span-2 flex items-center gap-2">
            <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="sale-sold-toggle">
              <input type="checkbox" checked={!!f.sold} onChange={(e) => set("sold", e.target.checked)} className="w-4 h-4 accent-[#E63946]" />
              Segna come <b>Venduta</b>
            </label>
          </div>
          <div className="col-span-2">
            <Label className="text-xs text-muted-foreground">Foto (galleria)</Label>
            <SaleImagesUpload images={f.images || []} onChange={(imgs) => set("images", imgs)} />
          </div>
          <div className="col-span-2">
            <Label className="text-xs text-muted-foreground">Descrizione</Label>
            <textarea value={f.description} onChange={(e) => set("description", e.target.value)} rows={4} className="w-full bg-[#181A22] border border-white/10 rounded-md mt-1.5 p-3 text-sm" data-testid="sale-desc" />
          </div>
        </div>
        <DialogFooter><Button onClick={save} disabled={busy} className="bg-[#E63946] hover:bg-[#D62828] text-white rounded-full" data-testid="save-sale-car-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Salva"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SaleImagesUpload({ images, onChange }) {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const handle = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    try {
      const uploaded = [];
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/admin/cars/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push(`${BACKEND}/api/car-images/${data.path}`);
      }
      onChange([...(images || []), ...uploaded]);
      toast.success(`${uploaded.length} foto caricate`);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); if (ref.current) ref.current.value = ""; }
  };
  const removeAt = (i) => onChange(images.filter((_, idx) => idx !== i));
  return (
    <div className="mt-1.5">
      <div className="grid grid-cols-3 gap-2">
        {images.map((src, i) => (
          <div key={i} className="relative aspect-[4/3] rounded-lg overflow-hidden border border-white/10 group" data-testid={`sale-img-${i}`}>
            <img src={src} alt="" className="w-full h-full object-cover" />
            <button type="button" onClick={() => removeAt(i)} className="absolute top-1 right-1 bg-black/70 hover:bg-[#E63946] text-white rounded-full p-1" data-testid={`sale-img-remove-${i}`}><X className="w-3.5 h-3.5" /></button>
          </div>
        ))}
        <button type="button" onClick={() => ref.current?.click()} className="aspect-[4/3] rounded-lg border-2 border-dashed border-white/15 hover:border-[#E63946]/60 transition-colors flex flex-col items-center justify-center gap-1 text-muted-foreground relative" data-testid="sale-img-upload-btn">
          {busy ? <Loader2 className="w-6 h-6 animate-spin" /> : <><ImagePlus className="w-6 h-6" /><span className="text-[10px]">Aggiungi foto</span></>}
        </button>
      </div>
      <input ref={ref} type="file" accept="image/*" multiple className="hidden" onChange={handle} />
    </div>
  );
}
